import React, { useState } from 'react';
import {
    Book,
    Award,
    CheckCircle,
    Download,
    Upload,
    FileText,
    Users,
    Clock
} from 'lucide-react';

const TrainingDemo = () => {
    // 模擬培訓數據
    const [trainingData, setTrainingData] = useState({
        availableCourses: [
            {
                id: 1,
                title: '數字化轉型策略',
                department: '管理層',
                duration: '6小時',
                format: '線上課程',
                status: 'ongoing',
                participants: 12,
                maxParticipants: 20
            },
            {
                id: 2,
                title: '客戶服務技巧提升',
                department: '客服部門',
                duration: '4小時',
                format: '混合式培訓',
                status: 'upcoming',
                participants: 8,
                maxParticipants: 15
            },
            {
                id: 3,
                title: '新版軟體開發工作坊',
                department: '技術研發部',
                duration: '8小時',
                format: '現場培訓',
                status: 'completed',
                participants: 15,
                maxParticipants: 15
            }
        ],
        personalTrainingRecord: [
            {
                id: 1,
                title: '專案管理認證課程',
                completionDate: '2024-02-15',
                certificate: 'PMP證書',
                score: 85
            },
            {
                id: 2,
                title: '企業溝通與領導力工作坊',
                completionDate: '2024-01-20',
                certificate: '結業證書',
                score: 92
            }
        ],
        trainingProgress: {
            totalCourses: 5,
            completedCourses: 3,
            inProgressCourses: 1,
            upcomingCourses: 1
        }
    });

    // 處理課程報名
    const handleCourseRegistration = (courseId) => {
        const updatedCourses = trainingData.availableCourses.map(course =>
            course.id === courseId
                ? { ...course, participants: course.participants + 1 }
                : course
        );

        setTrainingData(prev => ({
            ...prev,
            availableCourses: updatedCourses
        }));
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：可用培訓課程 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <Book className="mr-2 text-primary-600" />
                        可用培訓課程
                    </h2>

                    <div className="space-y-4">
                        {trainingData.availableCourses.map((course) => (
                            <div
                                key={course.id}
                                className={`bg-white p-4 rounded-lg shadow-sm border-l-4 ${course.status === 'ongoing'
                                        ? 'border-green-500'
                                        : course.status === 'upcoming'
                                            ? 'border-blue-500'
                                            : 'border-gray-500'
                                    }`}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-semibold">{course.title}</h3>
                                    <span className={`px-2 py-1 rounded-full text-xs ${course.status === 'ongoing'
                                            ? 'bg-green-50 text-green-600'
                                            : course.status === 'upcoming'
                                                ? 'bg-blue-50 text-blue-600'
                                                : 'bg-gray-50 text-gray-600'
                                        }`}>
                                        {course.status === 'ongoing'
                                            ? '進行中'
                                            : course.status === 'upcoming'
                                                ? '即將開始'
                                                : '已完成'}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600 mb-2">
                                    <div className="flex items-center mb-1">
                                        <Users className="mr-2 text-primary-600" size={16} />
                                        <span>{course.department}</span>
                                    </div>
                                    <div className="flex items-center mb-1">
                                        <Clock className="mr-2 text-primary-600" size={16} />
                                        <span>{course.duration}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FileText className="mr-2 text-primary-600" size={16} />
                                        <span>{course.format}</span>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-3">
                                        <div
                                            className="bg-primary-600 h-2.5 rounded-full"
                                            style={{
                                                width: `${(course.participants / course.maxParticipants * 100).toFixed(2)}%`
                                            }}
                                        ></div>
                                    </div>
                                    <span className="text-sm text-gray-600">
                                        {course.participants}/{course.maxParticipants}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleCourseRegistration(course.id)}
                                    disabled={course.participants >= course.maxParticipants || course.status !== 'upcoming'}
                                    className={`w-full mt-3 py-2 rounded-lg transition-colors ${course.participants >= course.maxParticipants || course.status !== 'upcoming'
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-primary-600 text-white hover:bg-primary-700'
                                        }`}
                                >
                                    {course.participants >= course.maxParticipants
                                        ? '已額滿'
                                        : course.status !== 'upcoming'
                                            ? '不可報名'
                                            : '立即報名'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 右側：個人培訓記錄 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Award className="mr-2 text-primary-600" />
                        個人培訓記錄
                    </h3>

                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h4 className="font-semibold mb-3">培訓進度總覽</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                                <span className="block text-2xl font-bold text-green-600">
                                    {trainingData.trainingProgress.completedCourses}
                                </span>
                                <span className="text-sm text-gray-600">已完成課程</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                                <span className="block text-2xl font-bold text-blue-600">
                                    {trainingData.trainingProgress.inProgressCourses}
                                </span>
                                <span className="text-sm text-gray-600">進行中課程</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                                <span className="block text-2xl font-bold text-yellow-600">
                                    {trainingData.trainingProgress.upcomingCourses}
                                </span>
                                <span className="text-sm text-gray-600">即將開始</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                                <span className="block text-2xl font-bold text-primary-600">
                                    {trainingData.trainingProgress.totalCourses}
                                </span>
                                <span className="text-sm text-gray-600">總課程數</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md">
                        <div className="p-4 border-b">
                            <h4 className="font-semibold">已獲得證書</h4>
                        </div>
                        {trainingData.personalTrainingRecord.map((record) => (
                            <div
                                key={record.id}
                                className="p-4 border-b flex items-center justify-between hover:bg-gray-50"
                            >
                                <div>
                                    <div className="font-medium">{record.title}</div>
                                    <div className="text-sm text-gray-600">
                                        完成日期：{record.completionDate}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        證書：{record.certificate}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <span className="mr-2 text-sm font-semibold text-primary-600">
                                        {record.score}分
                                    </span>
                                    <button className="text-primary-600 hover:bg-primary-50 p-2 rounded-full">
                                        <Download size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">培訓與職業發展</h4>
                    <p className="text-sm text-gray-600">
                        持續學習，提升專業能力，推動個人與組織成長
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        個人學習計劃
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        上傳證書
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrainingDemo;